import { FC, useCallback, useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Filter, FilterChangeEvent } from '@progress/kendo-react-data-tools'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { Search } from 'lucide-react'

import { PermissionsEnum } from '../../../../../api/Identity/Types/PermissionsEnum'
import { ClientSubscriptionDetailBackOfficeDto } from '../../../../../api/Trading-Accounts/Types/ClientSubscriptionDetailBackOfficeDto'
import { ClientSubscriptionOverrideDto } from '../../../../../api/Trading-Accounts/Types/ClientSubscriptionOverrideDto'
import { FlexBox } from '../../../../../components/Box'
import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal'
import { Modal } from '../../../../../components/CustomModal'
import { getSubscriptionOverviewClientFields } from '../../../../../components/Filter/getSubscriptionOverviewClientFields'
import { Loading } from '../../../../../components/Loading'
import MoreButton, { MoreButtonItem } from '../../../../../components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from '../../../../../components/Toast/Toast'
import { normalizeFilter, useGridState } from '../../../../../features/DataGrid'
import { useApiClient } from '../../../../../utils/api/ApiClient'
import { TradingAccountsApiClient } from '../../../../../utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from '../../../../../utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../../../utils/api/useFetch'
import { AuthSessionContext } from '../../../../../utils/AuthContext'
import { hasPermission } from '../../../../../utils/permissionsValidator'
import { buildQuery } from '../../../../../utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from '../../../../../utils/TableStateContext'
import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'
import { clientDetailsSubscriptionsColumns } from './clientDetailsSubscriptionsColumns'
import {
  ClientSubscriptionChangeStatusForm,
  ClientSubscriptionStatusChangeSubmitValues,
} from './ClientSubscriptionChangeStatusForm'
import {
  ClientSubscriptionPriceOverrideForm,
  ClientSubscriptionPriceOverrideFormValues,
} from './ClientSubscriptionPriceOverrideForm'
import { SubscriptionRequestType } from './SubscriptionRequestType'
import { SubscriptionTimeInForce } from './SubscriptionTimeInForce'
import { SubscriptionTimeInForceForm } from './SubscriptionTimeInForceForm'
import {
  canCancelRequest,
  canRequestDowngrade,
  canRequestResubscription,
  canRequestUpgrade,
  isSubscriptionDeductingFundOrInProcess,
  isSubscriptionImmediateAndPending,
  subscriptionLevelHasActiveSubscriptionRequest,
} from './subscriptionUtils'

interface Props {
  clientData: ClientDetailedDto
}

type subscriptionActionType =
  | 'Unsubscribe'
  | 'Subscribe'
  | 'Cancel'
  | 'Upgrade'
  | 'Downgrade'
  | 'Resubscribe'

export const ClientDetailsSubscriptionsPage: FC<Props> = ({ clientData }) => {
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'or', filters: [] })
  const [MTNetworkData, setMTNetworkData] = useState<ClientSubscriptionDetailBackOfficeDto[]>([])
  const tradingAccountApiClient = useApiClient(TradingAccountsApiClient)
  const [editSubscription, setEditSubscription] =
    useState<ClientSubscriptionDetailBackOfficeDto | null>(null)
  const [priceOverrideModal, setPriceOverrideModal] =
    useState<ClientSubscriptionOverrideDto | null>(null)
  const [deleteSubscriptionOverrideRequest, setDeleteSubscriptionOverrideRequest] =
    useState<ClientSubscriptionOverrideDto | null>(null)
  const [subscriptionTimeInForceId, setSubscriptionTimeInForceId] = useState(
    SubscriptionTimeInForce.Immediate
  )
  const [subscriptionTimeInForceModal, setSubscriptionTimeInForceModal] = useState<
    ClientSubscriptionDetailBackOfficeDto | undefined
  >()
  const [isUpdateRequest, setIsUpdateRequest] = useState(false)
  const [isUnsubscribeRequest, setIsUnsubscribeRequest] = useState(false)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const setToast = useContext(ToastContext)
  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: ClientSubscriptionDetailBackOfficeDto
  }>({
    visible: false,
  })
  const { gridState } = useGridState()
  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    const resp = await tradingAccountApiClient.getSubscriptions(clientData.id, {
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    await tradingAccountApiClient.getSubscriptionsOverrides()
    setMTNetworkData(resp)
    return resp
  }, [
    clientData.id,
    gridState.pageSize,
    gridState.pageIndex,
    tableData?.skipCount,
    gridState.take,
    gridState.skip,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const onDeletePriceOverride = async (overrideId: string) => {
    try {
      await tradingAccountApiClient.deleteSubscriptionOverride(overrideId)
      setToast(successToast(`Subscription price override deleted`))
    } catch (e) {
      console.error(e)
      setToast(errorToast(`Error deleting subscription price override ${e}`))
    } finally {
      forceRefresh()
      setDeleteSubscriptionOverrideRequest(null)
    }
  }

  const onPriceOverride = async (values: ClientSubscriptionPriceOverrideFormValues) => {
    if (values.id) {
      await tradingAccountApiClient.updateSubscriptionOverride(values)
    } else {
      delete values.id
      await tradingAccountApiClient.addSubscriptionOverride(values)
    }
    setToast(successToast(`Subscription price override ${values.id ? 'updated' : 'created'}}`))
    setPriceOverrideModal(null)
    forceRefresh()
  }

  const onEditStatus = async (values: ClientSubscriptionStatusChangeSubmitValues) => {
    await tradingAccountApiClient.createSubscriptionRequest(values)
    setToast(successToast(`Subscription status created`))
    forceRefresh()
  }

  const setAuditLog = (
    clientSubscriptionDetailBackOfficeDto: ClientSubscriptionDetailBackOfficeDto
  ) => {
    setAuditLogModal({ data: clientSubscriptionDetailBackOfficeDto, visible: true })
  }
  const { isLoading } = useFetchOne(callback)

  const getActions = (gridCellProps: GridCellProps): MoreButtonItem[] => {
    const actions = [
      {
        name: 'Audit Log',
        onClick: () => setAuditLog(gridCellProps.dataItem),
      },
      {
        name: 'Price Override',
        disabled: !hasPermission(permissions, PermissionsEnum.CanAddSubscriptionOverrides),
        onClick: () =>
          setPriceOverrideModal(
            gridCellProps.dataItem.clientSubscriptionOverrideDto ?? {
              amount: gridCellProps.dataItem.price,
              subscriptionDetailId:
                gridCellProps.dataItem?.subscriptionRequest?.subscriptionDetailId ??
                gridCellProps.dataItem.id,
              clientId: clientData.id,
              id: '',
            }
          ),
      },
      ...(gridCellProps.dataItem.clientSubscriptionOverrideDto
        ? [
            {
              name: 'Delete price Override',
              disabled: !hasPermission(permissions, PermissionsEnum.CanDeleteSubscriptionOverrides),
              onClick: () =>
                setDeleteSubscriptionOverrideRequest(
                  gridCellProps.dataItem.clientSubscriptionOverrideDto
                ),
            },
          ]
        : []),
    ]

    return addAction(gridCellProps, actions)
  }

  const addAction = (gridCellProps: GridCellProps, actions: MoreButtonItem[]): MoreButtonItem[] => {
    const subscriptionDetail = gridCellProps.dataItem as ClientSubscriptionDetailBackOfficeDto
    if (subscriptionDetail.subscription.details.length === 1) {
      return addActionToSingleLevelProduct(gridCellProps, actions)
    }
    return addActionToMultiLevelProduct(gridCellProps, actions)
  }

  const addActionToMultiLevelProduct = (
    gridCellProps: GridCellProps,
    actions: MoreButtonItem[]
  ): MoreButtonItem[] => {
    const subscriptionDetail = gridCellProps.dataItem as ClientSubscriptionDetailBackOfficeDto

    if (
      !!subscriptionDetail.activeViaBundleSubscriptionRequest ||
      isSubscriptionImmediateAndPending(subscriptionDetail?.subscriptionRequest) ||
      isSubscriptionDeductingFundOrInProcess(subscriptionDetail?.subscriptionRequest)
    ) {
      return actions
    } else if (
      subscriptionLevelHasActiveSubscriptionRequest(subscriptionDetail) ||
      (subscriptionDetail.subscriptionRequest &&
        (canCancelRequest(subscriptionDetail.subscriptionRequest) ||
          canRequestResubscription(subscriptionDetail.subscriptionRequest)))
    ) {
      addActionToSingleLevelProduct(gridCellProps, actions)
    } else if (canRequestUpgrade(subscriptionDetail)) {
      return addSubscriptionAction(
        SubscriptionRequestType.Update,
        actions,
        subscriptionDetail,
        true
      )
    } else if (canRequestDowngrade(subscriptionDetail)) {
      return addSubscriptionAction(
        SubscriptionRequestType.Update,
        actions,
        subscriptionDetail,
        false
      )
    }

    return addActionToSingleLevelProduct(gridCellProps, actions)
  }

  const addActionToSingleLevelProduct = (
    gridCellProps: GridCellProps,
    actions: MoreButtonItem[]
  ): MoreButtonItem[] => {
    const subscriptionDetail = gridCellProps.dataItem as ClientSubscriptionDetailBackOfficeDto

    if (
      !!subscriptionDetail.activeViaBundleSubscriptionRequest ||
      isSubscriptionImmediateAndPending(subscriptionDetail?.subscriptionRequest) ||
      isSubscriptionDeductingFundOrInProcess(subscriptionDetail?.subscriptionRequest)
    ) {
      return actions
    } else if (!subscriptionDetail.subscriptionRequest) {
      return addSubscriptionAction(SubscriptionRequestType.Add, actions, subscriptionDetail)
    } else if (canRequestResubscription(subscriptionDetail.subscriptionRequest)) {
      return addSubscriptionAction(SubscriptionRequestType.Resubscribe, actions, subscriptionDetail)
    } else if (
      canCancelRequest(subscriptionDetail.subscriptionRequest) ||
      subscriptionLevelHasActiveSubscriptionRequest(subscriptionDetail)
    ) {
      return addSubscriptionAction(SubscriptionRequestType.Cancel, actions, subscriptionDetail)
    }

    return addSubscriptionAction(SubscriptionRequestType.Add, actions, subscriptionDetail)
  }

  const getActionName = (
    requestType: SubscriptionRequestType,
    subscriptionDetail: ClientSubscriptionDetailBackOfficeDto,
    isUpgrade?: boolean
  ): subscriptionActionType => {
    switch (requestType) {
      case SubscriptionRequestType.Cancel:
        return canCancelRequest(subscriptionDetail.subscriptionRequest!) ? 'Cancel' : 'Unsubscribe'
      case SubscriptionRequestType.Resubscribe:
        return 'Resubscribe'
      case SubscriptionRequestType.Add:
        return 'Subscribe'
      default:
        return isUpgrade ? 'Upgrade' : 'Downgrade'
    }
  }

  const addSubscriptionAction = (
    requestType: SubscriptionRequestType,
    actions: MoreButtonItem[],
    subscriptionDetail: ClientSubscriptionDetailBackOfficeDto,
    isUpgrade?: boolean
  ): MoreButtonItem[] => {
    const newAction: MoreButtonItem = {
      name: getActionName(requestType, subscriptionDetail, isUpgrade),
      onClick: () => {
        if (
          requestType === SubscriptionRequestType.Add ||
          requestType === SubscriptionRequestType.Update ||
          (requestType === SubscriptionRequestType.Cancel &&
            subscriptionDetail.subscriptionRequest &&
            !canCancelRequest(subscriptionDetail.subscriptionRequest))
        ) {
          setSubscriptionTimeInForceModal(subscriptionDetail)
          setIsUpdateRequest(requestType === SubscriptionRequestType.Update)
          setIsUnsubscribeRequest(requestType === SubscriptionRequestType.Cancel)
        } else {
          handleSubscriptionActionClick(requestType, subscriptionDetail)
        }
      },
    }

    return [newAction, ...actions]
  }

  const buildSubscriptionValues = (
    subscriptionDetail: ClientSubscriptionDetailBackOfficeDto,
    requestType: SubscriptionRequestType
  ): ClientSubscriptionStatusChangeSubmitValues => {
    return {
      clientId: clientData.id,
      subscriptionTimeInForceId:
        requestType === SubscriptionRequestType.Add ||
        requestType === SubscriptionRequestType.Update
          ? subscriptionTimeInForceId
          : canCancelRequest(subscriptionDetail.subscriptionRequest!)
            ? SubscriptionTimeInForce.Immediate
            : SubscriptionTimeInForce.BeginningOfNextMonth,
      subscriptionRequestTypeId: requestType,
      overrideOpenPositionsForCancel: requestType === SubscriptionRequestType.Cancel,
      subscriptions: [
        {
          subscriptionDetailId: subscriptionDetail.id,
          subscriptionRequestTypeId: requestType,
        },
      ],
    }
  }

  const handleSubscriptionActionClick = async (
    requestType: SubscriptionRequestType,
    subscriptionDetail: ClientSubscriptionDetailBackOfficeDto
  ) => {
    const values: ClientSubscriptionStatusChangeSubmitValues = buildSubscriptionValues(
      subscriptionDetail,
      requestType
    )
    await tradingAccountApiClient.createSubscriptionRequest(values)
    forceRefresh()
  }
  const handleTimeInForceModalClose = () => {
    setSubscriptionTimeInForceModal(undefined)
    setSubscriptionTimeInForceId(SubscriptionTimeInForce.Immediate)
    setIsUnsubscribeRequest(false)
  }
  const handleClickOnSubscribeOrUpdate = async () => {
    if (!subscriptionTimeInForceModal) return

    const subscriptionDetail = subscriptionTimeInForceModal
    let subscriptionRequestTypeId

    if (isUnsubscribeRequest) {
      subscriptionRequestTypeId = SubscriptionRequestType.Cancel
    } else {
      subscriptionRequestTypeId = isUpdateRequest
        ? SubscriptionRequestType.Update
        : SubscriptionRequestType.Add
    }

    const values: ClientSubscriptionStatusChangeSubmitValues = {
      clientId: clientData.id,
      subscriptionTimeInForceId: Number(subscriptionTimeInForceId),
      subscriptionRequestTypeId,
      overrideOpenPositionsForCancel: subscriptionRequestTypeId === SubscriptionRequestType.Cancel,
      subscriptions: [
        {
          subscriptionDetailId: subscriptionDetail.id,
          subscriptionRequestTypeId,
        },
      ],
    }

    setSubscriptionTimeInForceModal(undefined)
    setIsUnsubscribeRequest(false)

    await tradingAccountApiClient.createSubscriptionRequest(values)
    forceRefresh()
  }

  const handleSubscriptionTimeInForceIdChange = (id: number) => {
    setSubscriptionTimeInForceId(id)
  }

  const handleFilterChange = (event: FilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const onFilterFetch = async () => {
    const searchQuery = getSearchQuery(filter)

    try {
      const response = await tradingAccountApiClient.getSubscriptions(clientData.id, {
        search: searchQuery,
        pageIndex: gridState.pageIndex ?? 1,
        pageSize: gridState.pageSize ?? 10,
        skipCount: tableData?.skipCount !== false,
      })

      await tradingAccountApiClient.getSubscriptionsOverrides()
      setMTNetworkData(response)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  return (
    <>
      {subscriptionTimeInForceModal && (
        <Modal
          visible={!!subscriptionTimeInForceModal}
          width={768}
          onClose={handleTimeInForceModalClose}
        >
          <SubscriptionTimeInForceForm
            onClose={handleTimeInForceModalClose}
            onConfirm={handleClickOnSubscribeOrUpdate}
            onChange={handleSubscriptionTimeInForceIdChange}
            isDowngrade={canRequestDowngrade(subscriptionTimeInForceModal)}
            isUnsubscribe={isUnsubscribeRequest}
          />
        </Modal>
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          clientId={clientData.id}
          subscriptionId={auditLogModal.data.id}
        />
      )}
      <ClientNavigation isEdit={false} tab={ClientTab.SUBSCRIPTIONS} data={clientData} />
      {priceOverrideModal && (
        <Modal
          visible={!!priceOverrideModal}
          onClose={() => setPriceOverrideModal(null)}
          width={768}
        >
          <ClientSubscriptionPriceOverrideForm
            data={priceOverrideModal}
            clientId={clientData.id}
            onSubmit={onPriceOverride}
            onClose={() => setPriceOverrideModal(null)}
          />
        </Modal>
      )}
      {editSubscription && (
        <Modal visible={!!editSubscription} onClose={() => setEditSubscription(null)} width={768}>
          <ClientSubscriptionChangeStatusForm
            data={editSubscription}
            clientId={clientData.id}
            id={editSubscription.id}
            onSubmit={onEditStatus}
            onClose={() => setEditSubscription(null)}
          />
        </Modal>
      )}
      {deleteSubscriptionOverrideRequest && deleteSubscriptionOverrideRequest.id && (
        <ConfirmationModal
          yesButtonText='Submit'
          onYes={() => onDeletePriceOverride(deleteSubscriptionOverrideRequest?.id)}
          onCancel={() => setDeleteSubscriptionOverrideRequest(null)}
          visible={!!deleteSubscriptionOverrideRequest}
          renderTitle={
            <div>
              <p>Are you sure you want to delete the price override?</p>
            </div>
          }
        />
      )}
      <FlexBox alignItems='center' justifyContent='space-between'>
        <Filter
          style={{ zIndex: 0 }}
          value={filter}
          fields={getSubscriptionOverviewClientFields()}
          onChange={handleFilterChange}
        />
        <Button type='button' onClick={onFilterFetch} data-test='filter-search-button'>
          <Search />
          Filter Search
        </Button>
      </FlexBox>
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Loading isLoading={isLoading}>
          <section style={{ padding: '.5rem' }}>
            <Grid
              {...gridState}
              data={MTNetworkData}
              resizable
              total={MTNetworkData.length}
              style={{ zIndex: 0 }}
            >
              {clientDetailsSubscriptionsColumns
                .filter((c) => c.show)
                .map((column, idx) => (
                  <Column key={idx + 2} {...column} />
                ))}
              <Column
                width={50}
                title=''
                cell={(gridCellProps: GridCellProps) => (
                  <td>
                    <MoreButton items={getActions(gridCellProps)} />
                  </td>
                )}
              />
            </Grid>
          </section>
        </Loading>
      </TableStateContext.Provider>
    </>
  )
}
